/**
 * 服务能力左侧导航树组件
 * 可折叠、激活态
 */
import { useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Build as BuildIcon,
  Lightbulb as LightbulbIcon,
  Gavel as GavelIcon,
  School as SchoolIcon,
  Verified as VerifiedIcon,
  GridOn as GridIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
  DesignServices as DesignServicesIcon,
  Science as ScienceIcon,
  Business as BusinessIcon,
  CardMembership as CardMembershipIcon,
  FactCheck as FactCheckIcon,
  Handshake as HandshakeIcon,
  TrendingUp as TrendingUpIcon,
  MenuBook as MenuBookIcon,
  Quiz as QuizIcon,
  CastForEducation as CastForEducationIcon,
  Analytics as AnalyticsIcon,
  Assignment as AssignmentIcon,
  RequestQuote as RequestQuoteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CampaignIcon from '@mui/icons-material/Campaign';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useLocation } from 'react-router-dom';
import type { NavTreeNode } from '@/types/common';
import { useNavStore } from '@/stores/navStore';
import { api } from '@/api';
import { COLORS } from '@/theme/tokens';

/** 图标名称到组件的映射 */
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  build: BuildIcon,
  lightbulb: LightbulbIcon,
  gavel: GavelIcon,
  school: SchoolIcon,
  verified: VerifiedIcon,
  grid: GridIcon,
  folder: FolderIcon,
  group: GroupIcon,
  category: CategoryIcon,
  design_services: DesignServicesIcon,
  science: ScienceIcon,
  business: BusinessIcon,
  card_membership: CardMembershipIcon,
  fact_check: FactCheckIcon,
  handshake: HandshakeIcon,
  trending_up: TrendingUpIcon,
  menu_book: MenuBookIcon,
  quiz: QuizIcon,
  cast_for_education: CastForEducationIcon,
  analytics: AnalyticsIcon,
  assignment: AssignmentIcon,
  request_quote: RequestQuoteIcon,
  account_balance_wallet: AccountBalanceWalletIcon,
  emoji_events: EmojiEventsIcon,
  campaign: CampaignIcon,
  receipt_long: ReceiptLongIcon,
  delete_outline: DeleteOutlineIcon,
  download: DownloadIcon,
  history: HistoryIcon,
  dashboard: DashboardIcon,
  settings: SettingsIcon,
  description: DescriptionIcon,
};

/** NavTree Props */
export interface NavTreeProps {
  /** 模块标识，默认 'service-capability' */
  module?: string;
  /** 模块标题，默认 '服务能力' */
  title?: string;
}

/**
 * NavTree 通用导航树组件
 * 根据模块加载对应导航数据，可折叠/展开，激活态高亮
 */
export function NavTree({ module = 'service-capability', title = '服务能力' }: NavTreeProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeNav, sidebarExpanded, setActiveNav, toggleSidebar, initExpanded } = useNavStore();
  const [navTreeData, setNavTreeData] = useState<NavTreeNode[]>([]);

  /** 加载导航树数据：根据当前 activeNav 决定默认展开哪一项 */
  useEffect(() => {
    api.getNavTree(module).then((treeData) => {
      setNavTreeData(treeData);
      // 找到当前 activeNav 所属的父级 id
      const allIds = treeData.map((n) => n.id);
      let expandId: string | undefined;
      const currentPath = location.pathname;
      for (const node of treeData) {
        if (node.children?.some((c) => c.route === currentPath)) {
          expandId = node.id;
          break;
        }
        if (node.children && node.children.length > 0) {
          // 一级菜单有自己的 route 时也算跳转
          if (node.route === currentPath) expandId = node.id;
        }
      }
      // 同步 activeNav 到 location.pathname
      if (currentPath && currentPath !== activeNav) {
        setActiveNav(currentPath);
      }
      initExpanded(allIds, expandId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  /** 处理一级菜单点击：有子项 → 跳到第一个子项 + 展开；无子项但有 route → 跳转；否则切换展开 */
  const handleTopLevelClick = (node: NavTreeNode): void => {
    // 1. 有子项 → 跳到第一个子项 + 展开该项
    if (node.children && node.children.length > 0) {
      const firstChild = node.children[0];
      setActiveNav(firstChild.route);
      navigate(firstChild.route);
      const allIds = navTreeData.map((n) => n.id);
      // 强制展开当前项（即使原本是收起）
      const isCurrentlyExpanded = sidebarExpanded[node.id] ?? false;
      if (!isCurrentlyExpanded) {
        toggleSidebar(node.id, allIds);
      }
      return;
    }
    // 2. 有 route（叶子节点） → 跳转
    if (node.route && node.route.length > 0) {
      setActiveNav(node.route);
      navigate(node.route);
      return;
    }
    // 3. 否则切换展开
    const allIds = navTreeData.map((n) => n.id);
    toggleSidebar(node.id, allIds);
  };

  /** 渲染图标 */
  const renderIcon = (iconName: string, fontSize: 'small' | 'medium' = 'small') => {
    const IconComponent = ICON_MAP[iconName] ?? BuildIcon;
    return <IconComponent sx={{ fontSize: fontSize === 'small' ? 18 : 20 }} />;
  };

  /** 处理子项点击 */
  const handleChildClick = (route: string): void => {
    setActiveNav(route);
    navigate(route);
  };

  return (
    <Box sx={{ padding: '12px' }}>
      {/* 标题 */}
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 700,
          color: COLORS.textPrimary,
          padding: '0 12px 12px',
        }}
      >
        {title}
      </Typography>

      {/* 导航树 */}
      <List sx={{ padding: 0 }}>
        {navTreeData.map((node) => {
          const expanded = sidebarExpanded[node.id] ?? false;
          return (
            <Box key={node.id}>
              {/* 一级分类 */}
              <ListItemButton
                onClick={() => handleTopLevelClick(node)}
                sx={{
                  padding: '10px 12px',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: COLORS.primary }}>
                  {renderIcon(node.icon, 'medium')}
                </ListItemIcon>
                <ListItemText
                  primary={node.label}
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: COLORS.textPrimary,
                  }}
                />
                {expanded ? (
                  <ExpandMoreIcon sx={{ color: COLORS.textSecondary, fontSize: 20 }} />
                ) : (
                  <ChevronRightIcon sx={{ color: COLORS.textSecondary, fontSize: 20 }} />
                )}
              </ListItemButton>

              {/* 子项 */}
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List sx={{ padding: 0 }}>
                  {node.children?.map((child) => {
                    const isActive =
                      activeNav === child.route || location.pathname === child.route;
                    return (
                      <ListItemButton
                        key={child.id}
                        onClick={() => handleChildClick(child.route)}
                        sx={{
                          padding: '8px 12px 8px 36px',
                          fontSize: '14px',
                          color: isActive ? COLORS.primary : COLORS.textSecondary,
                          borderLeft: isActive
                            ? `3px solid ${COLORS.primary}`
                            : '3px solid transparent',
                          backgroundColor: isActive ? COLORS.primaryBg : 'transparent',
                          borderRadius: '0 6px 6px 0',
                          '&:hover': {
                            backgroundColor: COLORS.primaryBg,
                            color: COLORS.primary,
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{
                            fontSize: '14px',
                            color: 'inherit',
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
